import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LucideIconsModule } from '../../../shared/lucide-icons.module';
import { PrimengImports } from '../../../shared/primeng-imports';
import { firstValueFrom } from 'rxjs';
import { MessageService } from 'primeng/api';
import { UserService } from '../../../service/user.service';
import { AppSettingsService } from '../../../config/config-service';
import { TokenIdService } from '../../../service/token-id.service';
import { Router } from '@angular/router';
import { HomeService } from '../../../service/home.service';

@Component({
  selector: 'app-investment-dialog',
  standalone: true,
  imports: [CommonModule, LucideIconsModule, PrimengImports, FormsModule],
  templateUrl: './investment-dialog.component.html',
  styleUrl: './investment-dialog.component.css',
  providers: [MessageService]
})
export class InvestmentDialogComponent implements OnInit, OnDestroy {
  @Input() visible: boolean = false;
  @Input() portfolio: any = null;
  @Output() close = new EventEmitter<void>();
  @Input() allPortfolios: any[] = [];
  @Input() showPortfolioDropdown: boolean = true;

  // @Output() buy = new EventEmitter<{ portfolio: any, tickets: number }>();

  ticketCount: number = 1;  

  // Dropdown selection handler
  onPortfolioSelect(selected: any) {
    if (!selected) {
      this.portfolio = null;
      return;
    }
    this.portfolio = selected;
    this.showPortfolioDropdown = true;
    this.ticketCount = 1;
    this.clearWarning();
  }

  warning: string = '';
  loading: boolean = false;
  private warningTimeout: any;
  private apiUrl: string = '';

  constructor(
    private messageService: MessageService,
    private userService: UserService,
    private appSettingsService: AppSettingsService,
    private tokenIdService: TokenIdService,
    private router: Router,
    private homeService: HomeService
  ) {
    this.apiUrl = this.appSettingsService.getSettings().apiEndPoint || '';
  }

  ngOnInit() {
    this.reset();
    console.log(this.visible, 'visible')
  }

  ngOnDestroy() {
    if (this.warningTimeout) {
      clearTimeout(this.warningTimeout);
    }
  }

  get totalAmount(): number {
    return (this.portfolio?.ticketPrice || 0) * this.ticketCount;
  }

  get isInvestDisabled(): boolean {
    return !this.portfolio || 
           this.ticketCount <= 0 || 
           this.ticketCount > this.portfolio.availableTickets ||
           this.portfolio.availableTickets === 0 ||
           this.loading;
  }

  get maxTicketsReached(): boolean {
    return this.ticketCount >= (this.portfolio?.availableTickets || 0);
  }

  get minTicketsReached(): boolean {
    return this.ticketCount <= 1;
  }
 formatMonthsToYears(months: number): string {
    if (!months) return '';
    
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    
    if (years === 0) {
      return `${months} month${months !== 1 ? 's' : ''}`;
    } else if (remainingMonths === 0) {
      return `${years} year${years !== 1 ? 's' : ''}`;
    } else {
      return `${years} year${years !== 1 ? 's' : ''} ${remainingMonths} month${remainingMonths !== 1 ? 's' : ''}`;
    }
  }
  onClose() {
    this.clearWarning();
    this.close.emit();
    this.reset();
    this.portfolio = null;
    this.showPortfolioDropdown = true;
  }

  async onBuy() {
    if (this.isInvestDisabled) return;
    console.log("portfolio", this.portfolio);

    if (!this.portfolio) {
      this.showWarning('Portfolio information is not available.');
      this.messageService.add({ severity: 'warning', summary: 'Warning', detail: 'Portfolio information is not available.', life: 3000 });
      return;
    }
    if (this.ticketCount <= 0) {
      this.showWarning('Please select at least 1 ticket.');
      this.messageService.add({ severity: 'warning', summary: 'Warning', detail: 'Please select at least 1 ticket.', life: 3000 });

      return;
    }
    if (this.ticketCount > this.portfolio.availableTickets) {
      this.showWarning(`You cannot buy more than ${this.portfolio.availableTickets} tickets.`);
      this.messageService.add({ severity: 'warning', summary: 'Warning', detail: `You cannot buy more than ${this.portfolio.availableTickets} tickets.`, life: 3000 });

      return;
    }
    if (this.portfolio.availableTickets === 0) {
      this.showWarning('No tickets are currently available for this portfolio.');
      this.messageService.add({ severity: 'warning', summary: 'Warning', detail: 'No tickets are currently available for this portfolio.', life: 3000 });

      return;
    }
    const token = this.userService.getAuthToken();
    const userId = this.tokenIdService.getUserIdFromToken(token || '');
    if (!userId) {
      this.showWarning('User not authenticated.');
      this.messageService.add({ severity: 'warning', summary: 'Warning', detail: 'User not authenticated.', life: 3000 });
      return;
    }
    this.clearWarning();
    this.loading = true;
    try {
      const token = this.userService.getAuthToken();
      const decoded = this.tokenIdService.decodeToken(token || '');
      const userId = decoded?.id || decoded?._id || decoded?.userId;
      if (!userId) {
        this.messageService.add({ severity: 'error', summary: 'Not Authenticated', detail: 'Please login to invest.', life: 3000 });
        this.loading = false;
        return;
      }
      const payload = {
        userId: userId,
        portfolioId: this.portfolio._id || this.portfolio.id,
        amount: this.totalAmount,
        ticketsPurchased: this.ticketCount
      };
      await firstValueFrom(this.homeService.createTransaction(payload));
      this.messageService.add({ severity: 'success', summary: 'Investment Successful', detail: `You have successfully invested in ${this.portfolio.name}.`, life: 4000 });
      this.loading = false;
      // Navigate to transactions tab and close dialog
      try {
        await this.router.navigate(['/user/transactions']);
      } finally {
        this.onClose();
      }
    } catch (err: any) {
      this.messageService.add({ severity: 'error', summary: 'Investment Failed', detail: err?.error?.message || err?.message || 'Transaction failed. Please try again.', life: 5000 });
      this.loading = false;
    }
  }

  onTicketCountChange(val: number) {
    if (!this.portfolio) return;
    this.clearWarning();
    if (val < 1) {
      this.ticketCount = 1;
    } else if (val > this.portfolio.availableTickets) {
      this.ticketCount = this.portfolio.availableTickets;
      if (this.portfolio.availableTickets === 0) {
        this.showWarning('No tickets are available for this portfolio.');
      } else {
        this.showWarning(`Maximum ${this.portfolio.availableTickets} tickets available.`);
      }
    } else {
      this.ticketCount = val;
    }
  }

  private showWarning(message: string) {
    this.warning = message;
    if (this.warningTimeout) {
      clearTimeout(this.warningTimeout);
    }
    this.warningTimeout = setTimeout(() => {
      this.clearWarning();
    }, 5000);
  }

  private clearWarning() {
    this.warning = '';
    if (this.warningTimeout) {
      clearTimeout(this.warningTimeout);
      this.warningTimeout = null;
    }
  }

  private reset() {
    this.ticketCount = 1;
    this.clearWarning();
    this.loading = false;
  }

  // Utility methods for template
  getRiskLevelClass(): string {
    if (!this.portfolio) return '';
    
    switch (this.portfolio.riskLevel?.toLowerCase()) {
      case 'high': return 'risk-high';
      case 'medium': return 'risk-medium';
      case 'low': return 'risk-low';
      default: return 'risk-medium';
    }
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }

  getAvailabilityStatus(): { text: string, class: string } {
    if (!this.portfolio) return { text: 'N/A', class: '' };
    
    const available = this.portfolio.availableTickets || 0;
    const total = available + (this.portfolio.soldTickets || 0);
    const percentage = total > 0 ? (available / total) * 100 : 0;
    
    if (percentage === 0) {
      return { text: 'Sold Out', class: 'text-red-600' };
    } else if (percentage < 20) {
      return { text: 'Limited', class: 'text-orange-600' };
    } else if (percentage < 50) {
      return { text: 'Available', class: 'text-yellow-600' };
    } else {
      return { text: 'Good Availability', class: 'text-green-600' };
    }
  }
}